import '@1c/cades-plugin-api';
import { useState, useRef, useCallback } from 'react';
import moment from 'moment';

const alowedAlgorithmValues = ['1.2.643.2.2.19', '1.2.643.7.1.1.1.1', '1.2.643.7.1.1.1.2'];

const cadesplugin = window.cadesplugin;

export const useCades = () => {
  const [isCadesPluginOK, setsCadesPluginOK] = useState(false);
  const [isFindingCerts, setIsFindingCerts] = useState(false);
  const [certs, setCerts] = useState(null);

  const checkPlugin = useCallback(() => {
    cadesplugin.async_spawn(function*() {
      const about = yield cadesplugin.CreateObjectAsync('CAdESCOM.About');
      const pluginVersion = yield about.PluginVersion;
      setsCadesPluginOK(!!pluginVersion);
    });
  }, []);

  const wasPluginCheckRun = useRef(false);
  if (cadesplugin && !wasPluginCheckRun.current) {
    cadesplugin.then(() => {
      checkPlugin();
    });
    wasPluginCheckRun.current = true;
  }

  const findCerts = useCallback(() => {
    setIsFindingCerts(true);
    cadesplugin.async_spawn(function*() {
      let store;

      try {
        store = yield cadesplugin.CreateObjectAsync('CAdESCOM.Store');

        if (!store) {
          return;
        }

        yield store.Open();
      } catch (ex) {
        return;
      }

      let certs;
      let count;

      try {
        certs = yield store.Certificates;
        count = yield certs.Count;
      } catch (ex) {
        // pass
      }

      const certPromises = [];

      for (let i = 1; i <= count; i += 1) {
        certPromises.push(certs.Item(i));
      }

      certs = yield Promise.all(
        certPromises.map(async promise => {
          const cert = await promise;
          const validToDate = moment(await cert.ValidToDate);
          const validFromDate = moment(await cert.ValidFromDate);

          if (moment().isBefore(validFromDate)) {
            return null;
          }

          if (moment().isAfter(validToDate)) {
            return null;
          }

          const hasPrivateKey = await cert.HasPrivateKey();

          if (!hasPrivateKey) {
            return null;
          }

          let isValid = false;

          try {
            const validator = await cert.IsValid();
            isValid = await validator.Result;
          } catch (e) {
            // pass
          }

          if (!isValid) {
            return null;
          }

          const version = await cert.Version;

          if (version < 3) {
            return null;
          }

          const subjectName = await cert.SubjectName;

          const issuerName = await cert.IssuerName;
          const pubKey = await cert.PublicKey();
          const algorithm = await pubKey.Algorithm;
          const algorithmName = await algorithm.FriendlyName;
          const algorithmValue = await algorithm.Value;
          const privateKey = hasPrivateKey ? await cert.PrivateKey : null;
          const providerName = privateKey ? await privateKey.ProviderName : null;

          if (!alowedAlgorithmValues.includes(algorithmValue)) {
            return null;
          }

          return {
            cert,
            version,
            validToDate,
            validFromDate,
            subjectName,
            issuerName,
            algorithmName,
            providerName,
          };
        })
      );

      setCerts(certs.filter(Boolean));
      setIsFindingCerts(false);

      store.Close();
    });
  }, []);

  const signData = useCallback((cert, dataToSign) => {
    return new Promise((resolve, reject) => {
      cadesplugin.async_spawn(function* signData() {
        try {
          const signer = yield cadesplugin.CreateObjectAsync('CAdESCOM.CPSigner');
          yield signer.propset_Certificate(cert.cert);

          const signedData = yield cadesplugin.CreateObjectAsync('CAdESCOM.CadesSignedData');
          yield signedData.propset_ContentEncoding(cadesplugin.CADESCOM_BASE64_TO_BINARY);
          yield signedData.propset_Content(dataToSign);

          const signedMessage = yield signedData.SignCades(signer, cadesplugin.CADESCOM_CADES_BES);

          resolve(signedMessage);
        } catch (e) {
          reject(`Failed to create signature. Error: ${cadesplugin.getLastError(e)}`);
        }
      });
    });
  }, []);

  return [isCadesPluginOK, findCerts, isFindingCerts, certs, signData];
};

export const checkSubjectNameFormat = subjectName => subjectName.includes('='); // проверка использования формата ключ-значение. Судя по форуму КриптоПро, иногда в серте указывают просто значения через запятую

export const parseSubjectName = subjectName =>
  subjectName
    .replace(/,([^,=]*=)/g, '|||$1') // в строке есть два типа запятых: разделяющие пары ключ-значение, являются частью содержимого значений. Находим все запятые-разделители и заменяем их на уникальную комбинацию |||, которая в значениях появляться гипотетически не должна
    .split('||| ') // благодаря предыдущему шагу, разделяем пары ключ-значение по нашему кастомному разделителю (плюс пробел)
    .reduce((acc, item) => {
      const [key, value] = item.split('=');
      const formattedValue =
        value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : value; // убираем лишние опциональные кавычки вокруг значений
      acc[key] = formattedValue;
      return acc;
    }, {});
